<template>
  <div class="data-grid table-pad sticky-h-700">
    <div v-if="hasHeader" class="control-header rows justify-space-between align-items-center">
      <slot name="header">
        {{ actionsHeader }}
        <button v-if="allowAdd" class="round" type="button" @click="onSelect(null)">
          <i class="bi bi-plus-lg"></i>
        </button>
      </slot>
    </div>
    <table class="table-tracking" :class="{interactive: this.interactive}">
      <thead>
      <tr>
        <th
            v-for="(header, headerText) in headers"
            v-bind:key="headerProp(header)"
            :class="headerClass(header)"
            @click="setSort(headerProp(header))"
        >
          <slot
              v-if="headerSlotName(header) != null"
              :name="headerSlotName(header)"
              :header-text="headerText"
              :prop="headerProp(header)"
          ></slot>
          <template v-else>{{ headerText }}</template>
          <i
              v-if="sortBy === headerProp(header)"
              :class="['bi', sortType === 'ASC' ? 'bi bi-arrow-down-short' : 'bi bi-arrow-up-short']"
          ></i>
        </th>
        <th v-if="supportsActionButtons" class="narrow">
          <span v-if="showActionsTableHeader">{{ $t('generic.actions') }}</span>
        </th>
      </tr>
      </thead>
      <tbody>
      <tr :id="item.id"
          v-for="item in dataRefined"
          v-bind:key="item.id"
          :class="item.classes"
          @click="onClickRow(item.id)"
      >
        <td
            v-for="header in headers"
            v-bind:key="headerProp(header)"
            :class="[columnClass(headerProp(header)), item[headerProp(header)]?.class]"
        >
          <slot
              v-if="item[headerProp(header)]?.slotName != null"
              :name="item[headerProp(header)].slotName"
              :item="item.dataObject"
              :index="item.index"
              :value="item[headerProp(header)]"
              :row="item"
          ></slot>
          <i v-else-if="item[headerProp(header)]?.icon != null" :class="item[headerProp(header)].icon"></i>
          <span v-else-if="item[headerProp(header)]?.checkBox != null">
            <input type="checkbox">
          </span>
          <span v-else>{{ item[headerProp(header)]?.message ?? item[headerProp(header)] }}</span>
        </td>
        <td v-if="supportsActionButtons" :class="actionsCellClass">
          <button v-if="allowEdit" class="transparent table-action" @click="onSelect(item.id)">
            <i class="bi bi-pencil-fill"></i>
          </button>
          <button v-if="allowDelete" class="transparent table-action" @click="onDelete(item.id)">
            <i class="bi bi-trash"></i>
          </button>
          <button v-if="allowAddInList" class="transparent table-action" @click="onAddOnList(item.id, $event)">
            <i class="bi bi-plus-lg"></i>
          </button>
          <button v-if="allowRemoveFromList" class="transparent table-action" @click="onRemoveFromList(item.id, $event)">
            <i class="bi bi-trash"></i>
          </button>
          <button
              v-for="(action, actionIndex) in getVisibleActions(item.dataObject)"
              v-bind:key="`${action.icon}-${actionIndex}`"
              class="transparent table-action"
              :class="actionClass(action, item.dataObject)"
              :title="actionTitle(action, item.dataObject)"
              @click="action.handler(item.dataObject)"
          >
            <i :class="action.icon"/>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {dataAggregator} from '@/assets/js/data-manipulation';

export default {
  name: 'DataGrid',
  props: {
    selectedId: {
      type: [Number, String, null],
      default: null,
    },
    actionsHeader: {
      type: [String, null],
      default: null,
    },
    showActionsTableHeader: {
      type: [Boolean],
      default: true,
    },
    interactive: {type: Boolean, default: true},
    allowAdd: {type: Boolean, default: false},
    allowEdit: {type: Boolean, default: false},
    allowDelete: {type: Boolean, default: false},
    allowSelect: {type: Boolean, default: false},
    allowAddInList: {type: Boolean, default: false},
    allowRemoveFromList: {type: Boolean, default: false},
    data: Array,
    dataModel: Object,
    defaultSortBy: {
      type: [String, null],
      default: null,
    },
  },
  data() {
    return {
      sortBy: null,
      sortType: 'ASC',
    };
  },
  computed: {
    hasHeader() {
      return this.$slots.header !== undefined || (this.actionsHeader !== null && this.allowAdd);
    },
    supportsActionButtons() {
      return this.allowEdit || this.allowDelete || this.allowAddInList || this.actions.length > 0 || this.allowRemoveFromList;
    },
    headers() {
      return this.dataModel['headers'] || {};
    },
    values() {
      return this.dataModel['values'] || {};
    },
    actions() {
      return this.dataModel['actions'] || [];
    },
    actionsCellClass() {
      return this.dataModel['actionsCellClass'] || '';
    },
    columnClasses() {
      return this.dataModel['columnClasses'] || {};
    },
    dataRefined() {
      let data = this.data.reduce(
          (result, item, index) => {
            let refinedRow = {id: item.id};
            Object.values(this.headers).forEach(header => {
              const prop = this.headerProp(header);
              refinedRow[prop] = this.valueFor(item, prop, index);
            });
            refinedRow.index = index;
            refinedRow.classes = this.getRowClass(item, index);
            refinedRow.dataObject = item;
            result.push(refinedRow);

            return result;
          },
          [],
      );

      if (this.sortBy !== null) {
        data = dataAggregator.sortList(data, this.sortBy, this.sortType);
      }

      return data;
    },
  },
  mounted() {
    if (this.defaultSortBy !== null) {
      this.sortBy = this.defaultSortBy;
    }
  },
  methods: {
    headerProp(header) {
      return header?.prop ?? header;
    },
    headerSlotName(header) {
      return header?.slotName ?? null;
    },
    setSort(col) {
      if (this.sortBy === col) {
        this.sortType = this.sortType === 'ASC' ? 'DESC' : 'ASC';
      }

      this.sortBy = col;
    },
    valueFor(item, prop, index) {
      return typeof this.values[prop] === 'function'
          ? this.values[prop](item, index)
          : item[prop];
    },
    getRowClass(item, index) {
      let result = [];

      if (this.dataModel['class']) {
        result = typeof this.dataModel['class'] === 'function'
            ? this.dataModel['class'](item, index)
            : this.dataModel['class'];
      }

      if (item.id === this.selectedId) {
        result.push('active');
      }

      return result;
    },
    onClickRow(id) {
      if (!this.allowSelect) {
        return;
      }

      this.onSelect(id);
    },
    onSelect(id) {
      this.$emit('on-select', id);
    },
    onDelete(id) {
      this.$emit('on-delete', id);
    },
    onAddOnList(id, event){
      event.preventDefault();
      event.stopPropagation();
      this.$emit('on-add-on-list', id);
    },
    onRemoveFromList(id, event){
      event.preventDefault();
      event.stopPropagation();
      this.$emit('on-remove-from-list', id);
    },
    getVisibleActions(item) {
      return this.actions.filter(action => this.isActionVisible(action, item));
    },
    isActionVisible(action, item) {
      if (typeof action?.isVisible !== 'function') {
        return true;
      }

      return Boolean(action.isVisible(item));
    },
    actionClass(action, item) {
      if (typeof action?.buttonClass === 'function') {
        return action.buttonClass(item);
      }

      return action?.buttonClass;
    },
    actionTitle(action, item) {
      if (typeof action?.title === 'function') {
        return action.title(item);
      }

      return action?.title ?? null;
    },
    headerClass(header) {
      return header?.class || this.columnClasses[this.headerProp(header)] || '';
    },
    columnClass(prop) {
      return this.columnClasses[prop] || '';
    },
    scrollToSelectedRow() {
      if (!this.selectedId) {
        return;
      }

      const row = document.getElementById(this.selectedId);
      row?.scrollIntoView({
        behavior: 'instant',
        block: 'nearest'
      });
    }
  },
};
</script>

<style scoped lang="scss">
@import '@/assets/scss/variables.scss';

.data-grid {
  .control-header {
    color: $default-text-color;
    padding: 20px 20px 20px 20px;
  }

  &.strech {
    width: 100%;
  }
}

table.table-tracking th,
table.table-tracking td {
  word-break: normal;
  overflow-wrap: normal;
  hyphens: none;
}
</style>
